import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Flex,
  SimpleGrid,
  Stack,
  Spinner,
  NumberInput,
  NumberInputField,
  Badge,
  Progress,
  Divider,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  useColorModeValue,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import ProductPlannerApi from "../../api/productPlanner";

const plannerBudgets = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80];
const MAX_ITEMS_PER_PLAN = 5;

// اندازه فونت‌ها و عرض‌ها
const tinyFont = "2xs";
const headerFont = "11px";
const cellFont = "13px";
const nameFont = "14px";

const OptimizePurchaseModal = ({
  isOpen,
  onClose,
  onOptimize,
  optimize,
  setOptimize,
  optimizeResult,
  loading,
}) => {
  const [autoPlans, setAutoPlans] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);

  const [expandedPlans, setExpandedPlans] = useState(new Set());
  const togglePlanExpand = (budget) => {
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(budget)) next.delete(budget);
      else next.add(budget);
      return next;
    });
  };

  const [manualExpanded, setManualExpanded] = useState(false);
  const toggleManualExpand = () => setManualExpanded((prev) => !prev);

  // پالت و پس‌زمینه‌های جدید جذاب‌تر
  const plannerModalBg = useColorModeValue(
    undefined,
    undefined
  ); // content خودش گرادینت می‌گیرد
  const modalBgGradient = useColorModeValue(
    "linear(to-br, purple.50, white)",
    "linear(to-br, gray.800, gray.900)"
  );
  const autoResultBg = useColorModeValue("white", "gray.900");
  const subText = useColorModeValue("gray.600", "gray.300");
  const accent = useColorModeValue("purple.500", "purple.300");
  const plannerBorderNeutral = useColorModeValue("purple.100", "whiteAlpha.300");

  const runAutoPlans = useCallback(async () => {
    setAutoLoading(true);
    setAutoPlans(
      plannerBudgets.map((budget) => ({
        budget,
        loading: true,
        result: null,
        error: null,
      }))
    );
    for (const budget of plannerBudgets) {
      try {
        const res = await ProductPlannerApi.optimizePurchases(budget, MAX_ITEMS_PER_PLAN);
        setAutoPlans((prev) =>
          prev.map((plan) =>
            plan.budget === budget
              ? { ...plan, loading: false, result: res.data || null }
              : plan
          )
        );
      } catch (error) {
        setAutoPlans((prev) =>
          prev.map((plan) =>
            plan.budget === budget
              ? { ...plan, loading: false, error: "عدم دریافت داده" }
              : plan
          )
        );
      }
    }
    setAutoLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      runAutoPlans();
      setOptimize({ budget: "30", max_items: "5" });
      setExpandedPlans(new Set());
      setManualExpanded(false);
    }
  }, [isOpen, runAutoPlans, setOptimize]);

  const handleResetPlanner = () => {
    runAutoPlans();
    setOptimize({ budget: "30", max_items: "5" });
    setExpandedPlans(new Set());
    setManualExpanded(false);
  };

  const activeManualCombination =
    optimizeResult?.topCombinations?.length > 0 ? optimizeResult.topCombinations : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        bgGradient={modalBgGradient}
        maxW="95rem"
        maxH="92vh"
        borderRadius="2xl"
        boxShadow="xl"
        borderWidth="1px"
        borderColor={useColorModeValue("purple.100", "whiteAlpha.200")}
      >
        <ModalHeader
          display="flex"
          flexDirection="row-reverse"
          justifyContent="space-between"
          alignItems="center"
          py={3}
        >
          <Box flex={1} textAlign="center">
            <Text
              bgGradient={useColorModeValue(
                "linear(to-r, purple.600, pink.500)",
                "linear(to-r, purple.300, pink.300)"
              )}
              bgClip="text"
              fontWeight="extrabold"
            >
              خرید بهینه
            </Text>
          </Box>
          <ModalCloseButton position="absolute" left="12px" right="unset" />
        </ModalHeader>

        <ModalBody pb={2} pt={0}>
          <Text fontSize="sm" color={subText} mb={4}>
            محاسبات خرید بهینه برای طیف گسترده‌ای از بودجه‌ها به صورت خودکار انجام می‌شود تا بتوانید سناریوها را کنار هم مقایسه کنید.
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 5 }} gap={3}>
            {autoPlans.map((plan) => {
              const bestCombination = plan.result?.topCombinations?.[0] || null;
              const totalSpent = bestCombination ? Number(bestCombination.spent) : 0;
              const remaining = bestCombination ? Number(bestCombination.remaining) : plan.budget;
              const spentPercent =
                bestCombination && plan.budget > 0 ? Math.min((totalSpent / plan.budget) * 100, 100) : 0;

              const totalCombos = plan.result?.topCombinations?.length || 0;
              const isExpanded = expandedPlans.has(plan.budget);
              const visibleCount = isExpanded ? totalCombos : 4;

              // رنگ کارت بر اساس وضعیت بودجه
              const borderColor = plan.loading
                ? plannerBorderNeutral
                : remaining >= 0
                ? useColorModeValue("green.200", "green.400")
                : useColorModeValue("red.200", "red.400");

              const bgGradient = useColorModeValue(
                remaining >= 0
                  ? "linear(to-br, green.50, white)"
                  : "linear(to-br, red.50, white)",
                remaining >= 0
                  ? "linear(to-br, gray.700, gray.800)"
                  : "linear(to-br, gray.750, gray.850)"
              );

              return (
                <Box
                  key={plan.budget}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="2xl"
                  bgGradient={bgGradient}
                  p={3}
                  boxShadow={useColorModeValue("sm", "md")}
                  display="flex"
                  flexDirection="column"
                  gap={3}
                  transition="all 0.2s ease-in-out"
                  _hover={{
                    boxShadow: useColorModeValue("lg", "xl"),
                    transform: "translateY(-3px)",
                  }}
                >
                  <Flex align="center" justify="space-between">
                    <Box>
                      <Text fontWeight="bold" fontSize="md" color={useColorModeValue("gray.800", "white")}>
                        بودجه {plan.budget} دلاری
                      </Text>
                      <Text fontSize="xs" color={subText}>
                        حداکثر {MAX_ITEMS_PER_PLAN} آیتم
                      </Text>
                    </Box>
                    {plan.loading ? (
                      <Badge
                        borderRadius="lg"
                        px={2}
                        py={1}
                        fontSize="0.7rem"
                        bgGradient={useColorModeValue(
                          "linear(to-r, purple.400, pink.400)",
                          "linear(to-r, purple.500, pink.500)"
                        )}
                        color="white"
                      >
                        در حال محاسبه
                      </Badge>
                    ) : (
                      <Badge
                        borderRadius="lg"
                        px={3}
                        py={1}
                        fontSize="0.75rem"
                        bgGradient={
                          remaining >= 0
                            ? useColorModeValue(
                                "linear(to-r, green.400, teal.400)",
                                "linear(to-r, green.500, teal.500)"
                              )
                            : useColorModeValue(
                                "linear(to-r, red.400, pink.400)",
                                "linear(to-r, red.500, pink.500)"
                              )
                        }
                        color="white"
                      >
                        {remaining >= 0
                          ? `مانده ${remaining.toFixed(2)}$`
                          : `کسری ${Math.abs(remaining).toFixed(2)}$`}
                      </Badge>
                    )}
                  </Flex>

                  {plan.loading ? (
                    <Flex align="center" justify="center" flex={1} minH="80px">
                      <Spinner size="sm" color={accent} />
                    </Flex>
                  ) : plan.error ? (
                    <Flex align="center" justify="center" flex={1} minH="80px">
                      <Text fontSize="xs" color="red.400">
                        {plan.error}
                      </Text>
                    </Flex>
                  ) : bestCombination ? (
                    <>
                      <Box>
                        <Flex justify="space-between" fontSize="xs" mb={1}>
                          <Text color={subText}>مجموع هزینه</Text>
                          <Text fontWeight="bold" color={remaining >= 0 ? "green.600" : "red.500"}>
                            {totalSpent.toFixed(2)}$
                          </Text>
                        </Flex>
                        <Progress
                          value={spentPercent}
                          colorScheme={remaining >= 0 ? "green" : "red"}
                          borderRadius="md"
                          height="7px"
                          bg={useColorModeValue("blackAlpha.100", "whiteAlpha.200")}
                        />
                      </Box>

                      <Stack spacing={2}>
                        {plan.result.topCombinations.slice(0, visibleCount).map((combo, idx) => (
                          <Box
                            key={idx}
                            borderWidth="1px"
                            borderRadius="xl"
                            borderColor={useColorModeValue("purple.200", "whiteAlpha.300")}
                            bg={autoResultBg}
                            p={2}
                            boxShadow={useColorModeValue("xs", "sm")}
                          >
                            <Flex justify="space-between" align="center" mb={1}>
                              <Text fontSize={tinyFont} color={useColorModeValue("gray.700", "gray.200")} fontWeight="bold">
                                سناریو {idx + 1}
                              </Text>
                              <Text fontSize="12px" fontWeight="semibold" color={useColorModeValue("purple.700", "purple.300")}>
                                باقی‌مانده: {Number(combo.remaining).toFixed(2)}$
                              </Text>
                            </Flex>
                            <Table
                              size="xs"
                              variant="simple"
                              sx={{
                                th: {
                                  py: 1,
                                  px: 2,
                                  fontSize: headerFont,
                                  fontWeight: "bold",
                                  bg: useColorModeValue("purple.50", "whiteAlpha.100"),
                                  color: useColorModeValue("purple.800", "purple.200"),
                                },
                                td: { py: 1, px: 2, fontSize: cellFont },
                              }}
                            >
                              <Thead>
                                <Tr>
                                  <Th w="55%">نام</Th>
                                  <Th isNumeric>قیمت</Th>
                                  <Th isNumeric>تعداد</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {combo.combination.map((item, itemIdx) => (
                                  <Tr key={itemIdx} _hover={{ bg: useColorModeValue("blackAlpha.50", "whiteAlpha.100") }}>
                                    <Td>
                                      <Text fontSize={nameFont} fontWeight="semibold">
                                        {item.name}
                                      </Text>
                                    </Td>
                                    <Td isNumeric fontWeight="semibold">{item.price}</Td>
                                    <Td isNumeric fontWeight="semibold">{item.suggested_count}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        ))}

                        {totalCombos > 4 && (
                          <Flex justify="center">
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="purple"
                              onClick={() => togglePlanExpand(plan.budget)}
                              _hover={{ bg: useColorModeValue("purple.50", "whiteAlpha.200") }}
                            >
                              {isExpanded ? "نمایش کمتر" : "نمایش بیشتر"}
                            </Button>
                          </Flex>
                        )}
                      </Stack>
                    </>
                  ) : (
                    <Flex align="center" justify="center" flex={1}>
                      <Text fontSize="xs" color="gray.400">
                        نتیجه‌ای یافت نشد
                      </Text>
                    </Flex>
                  )}
                </Box>
              );
            })}
          </SimpleGrid>

          <Divider my={5} />

          {/* محاسبه دستی - بدون تغییر در عرض ستون، اما با رنگ‌بندی هماهنگ */}
          <Box
            borderWidth="1px"
            borderRadius="2xl"
            borderColor={useColorModeValue("purple.100", "whiteAlpha.200")}
            p={4}
            bgGradient={useColorModeValue("linear(to-br, purple.25, white)", "linear(to-br, gray.800, gray.900)")}
            boxShadow={useColorModeValue("sm", "md")}
          >
            <Flex align="center" justify="space-between" mb={3}>
              <Box>
                <Text fontWeight="bold" fontSize="md" color={useColorModeValue("gray.800", "white")}>
                  محاسبه بهینه‌سازی دستی
                </Text>
                <Text fontSize="sm" color={subText}>
                  اگر بودجه خاصی مدنظر دارید، از طریق فرم زیر محاسبه را انجام دهید.
                </Text>
              </Box>
              {optimizeResult?.budget && (
                <Badge
                  borderRadius="lg"
                  px={3}
                  py={1}
                  fontSize="0.75rem"
                  bgGradient={useColorModeValue("linear(to-r, purple.500, pink.500)", "linear(to-r, purple.500, pink.500)")}
                  color="white"
                >
                  آخرین محاسبه: بودجه {optimizeResult.budget}$
                </Badge>
              )}
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={3} mb={3}>
              <FormControl>
                <FormLabel fontSize="sm" mb={1} color={useColorModeValue("gray.700", "gray.200")}>
                  بودجه دلخواه
                </FormLabel>
                <NumberInput
                  min={0}
                  value={optimize.budget}
                  onChange={(v) => setOptimize((p) => ({ ...p, budget: v }))}
                  size="sm"
                >
                  <NumberInputField placeholder="به دلار" />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={1} color={useColorModeValue("gray.700", "gray.200")}>
                  حداکثر تعداد آیتم
                </FormLabel>
                <NumberInput
                  min={1}
                  max={MAX_ITEMS_PER_PLAN}
                  value={optimize.max_items || MAX_ITEMS_PER_PLAN}
                  onChange={(v) => setOptimize((p) => ({ ...p, max_items: v }))}
                  size="sm"
                >
                  <NumberInputField placeholder="مثلاً ۵" />
                </NumberInput>
              </FormControl>
              <Flex align="flex-end" justify="flex-start">
                <Button
                  colorScheme="purple"
                  onClick={() => onOptimize()}
                  isLoading={loading}
                  size="sm"
                  bgGradient={useColorModeValue(
                    "linear(to-r, purple.500, pink.500)",
                    "linear(to-r, purple.500, pink.500)"
                  )}
                  _hover={{
                    opacity: 0.9,
                  }}
                >
                  محاسبه خرید بهینه
                </Button>
              </Flex>
            </SimpleGrid>

            {activeManualCombination ? (
              <>
                <Stack spacing={3}>
                  {(manualExpanded ? activeManualCombination : activeManualCombination.slice(0, 4)).map((combo, idx) => (
                    <Box
                      key={idx}
                      p={3}
                      borderWidth="1px"
                      borderRadius="xl"
                      bg={autoResultBg}
                      borderColor={useColorModeValue("purple.200", "whiteAlpha.300")}
                      boxShadow={useColorModeValue("xs", "sm")}
                    >
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontSize="sm" fontWeight="bold">
                          سناریو {idx + 1}
                        </Text>
                        <Flex gap={3} align="center">
                          <Text fontSize="sm">هزینه: {Number(combo.spent).toFixed(2)}$</Text>
                          <Text fontSize="11px" color={subText}>
                            مانده: {Number(combo.remaining).toFixed(2)}$
                          </Text>
                        </Flex>
                      </Flex>
                      <Table
                        size="sm"
                        sx={{
                          th: {
                            py: 1.5,
                            px: 2,
                            fontSize: headerFont,
                            fontWeight: "bold",
                            bg: useColorModeValue("purple.50", "whiteAlpha.100"),
                            color: useColorModeValue("purple.800", "purple.200"),
                          },
                          td: { py: 1.5, px: 2, fontSize: cellFont },
                        }}
                      >
                        <Thead>
                          <Tr>
                            <Th>نام</Th>
                            <Th isNumeric>قیمت</Th>
                            <Th isNumeric>تعداد پیشنهادی</Th>
                            <Th>تاریخ پایان تخفیف</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {combo.combination.map((item, i) => (
                            <Tr key={i} _hover={{ bg: useColorModeValue("blackAlpha.50", "whiteAlpha.100") }}>
                              <Td>
                                <Text fontSize={nameFont} fontWeight="semibold">
                                  {item.name}
                                </Text>
                              </Td>
                              <Td isNumeric fontWeight="semibold">{item.price}</Td>
                              <Td isNumeric fontWeight="semibold">{item.suggested_count}</Td>
                              <Td>
                                <Text fontSize="xs" color={subText}>
                                  {item.discount_end_date}
                                </Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  ))}
                </Stack>
                {activeManualCombination.length > 4 && (
                  <Flex justify="center" mt={2}>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="purple"
                      onClick={toggleManualExpand}
                      _hover={{ bg: useColorModeValue("purple.50", "whiteAlpha.200") }}
                    >
                      {manualExpanded ? "نمایش کمتر" : "نمایش بیشتر"}
                    </Button>
                  </Flex>
                )}
              </>
            ) : (
              <Text fontSize="sm" color="gray.400">
                هنوز محاسبه‌ای انجام نشده است.
              </Text>
            )}
          </Box>
        </ModalBody>

        <ModalFooter py={3}>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleResetPlanner}
            leftIcon={<RepeatIcon />}
            isLoading={autoLoading}
            size="sm"
            _hover={{ bg: useColorModeValue("purple.50", "whiteAlpha.200") }}
          >
            بازنشانی محاسبات خودکار
          </Button>
          <Button
            colorScheme="purple"
            onClick={onClose}
            size="sm"
            bgGradient={useColorModeValue("linear(to-r, purple.500, pink.500)", "linear(to-r, purple.500, pink.500)")}
            _hover={{ opacity: 0.9 }}
          >
            بستن پنجره
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OptimizePurchaseModal;
